import { createSelector } from '@reduxjs/toolkit';

import { selectConversationsQuota } from '../conversationsQuota';
import { selectSettings, settingsModel } from '../settings';
import { selectFlags } from '../flags';

export const maybeWidgetOnline = createSelector(
  selectSettings.assistantOnline,
  selectConversationsQuota.isExceeding,
  (assistantOnline, isExceeding) => {
    if (assistantOnline === undefined) {
      return;
    }
    if (isExceeding === undefined) {
      return;
    }

    return assistantOnline && !isExceeding;
  },
);

const { OfflineBehavior } = settingsModel;

export const shouldShowWidget = createSelector(
  selectSettings.offlineStrategy,
  maybeWidgetOnline,
  (strategy, isOnline) => {
    return (
      isOnline || (strategy !== undefined && strategy !== OfflineBehavior.HIDE)
    );
  },
);

export const offlineDisabledMessaging = createSelector(
  maybeWidgetOnline,
  selectSettings.offlineStrategy,
  (online, strategy) =>
    !online && strategy === OfflineBehavior.CONTACT_FORM_WITHOUT_CHAT,
);

export const offlineShouldShowContactForm = createSelector(
  maybeWidgetOnline,
  selectSettings.offlineStrategy,
  (online, strategy) =>
    !online &&
    (strategy === OfflineBehavior.CONTACT_FORM_WITHOUT_CHAT ||
      strategy === OfflineBehavior.CONTACT_FORM_WITH_CHAT),
);

export const shouldDisableInput = createSelector(
  maybeWidgetOnline,
  selectSettings.offlineStrategy,
  selectFlags.isContact,
  (online, strategy, isContact) =>
    !online &&
    strategy === OfflineBehavior.CONTACT_FORM_WITHOUT_CHAT &&
    !isContact,
);

export const introMessage = createSelector(
  selectSettings.introMessageSettings,
  maybeWidgetOnline,
  (introMessageSettings, online) =>
    online && introMessageSettings?.enabled && introMessageSettings?.text,
);

export const legalMessage = createSelector(
  selectSettings.legalMessageSettings,
  maybeWidgetOnline,
  (legalMessageSettings, online) =>
    online && legalMessageSettings?.enabled && legalMessageSettings?.text,
);

export const engageMessage = createSelector(
  selectSettings.engageMessageSettings,
  maybeWidgetOnline,
  (engageMessageSettings, online) =>
    online && engageMessageSettings?.enabled && engageMessageSettings?.text,
);

export const offlineMessage = createSelector(
  selectSettings.offlineMessageSettings,
  maybeWidgetOnline,
  (offlineMessageSettings, online) =>
    !online && offlineMessageSettings?.enabled && offlineMessageSettings?.text,
);

export const shouldAutoOpenWidget = createSelector(
  selectSettings.widgetSettings,
  maybeWidgetOnline,
  (settings, isOnline) => settings?.enabled && settings?.autoOpen && isOnline,
);

export const suggestions = createSelector(
  selectSettings.suggestionsSettings,
  maybeWidgetOnline,
  (suggestionsSettings, online) =>
    online &&
    suggestionsSettings?.enabled &&
    suggestionsSettings?.questions &&
    suggestionsSettings?.questions.length > 0
      ? suggestionsSettings.questions
      : undefined,
);

export const suggestionsIntroMessage = createSelector(
  selectSettings.suggestionsSettings,
  maybeWidgetOnline,
  (suggestionsSettings, online) =>
    online &&
    suggestionsSettings?.enabled &&
    suggestionsSettings?.introduction?.text
      ? suggestionsSettings.introduction.text
      : undefined,
);
